import CountUp from 'react-countup';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetScreeningStats } from '@/reactQuery/get';
import useTranslation from '@/hooks/useTranslation';
import StatisticCard from './StatisticCard';

interface ScreeningInsightsProps {
  projectId: string;
}

function ScreeningInsights({ projectId }: ScreeningInsightsProps) {
  const { currentTheme } = useTheme();
  const { lang } = useTranslation();
  const { data, isLoading } = useGetScreeningStats(projectId);

  const quotaPercentage = data?.quota?.percentage ?? 0;

  const progressBarColor =
    quotaPercentage >= 100
      ? 'bg-red-500'
      : quotaPercentage >= 80
        ? 'bg-amber-500'
        : 'bg-emerald-500';

  if (isLoading) {
    return (
      <div className="flex flex-col w-full gap-8">
        <Skeleton className="h-6 w-48 rounded" />
        <Skeleton className="h-10 w-full rounded-full" />
        <div className="grid grid-cols-5 gap-4 max1100:grid-cols-3 md800:grid-cols-2 min500:grid-cols-1">
          <Skeleton className="w-full h-40 rounded-2xl" />
          <Skeleton className="w-full h-40 rounded-2xl" />
          <Skeleton className="w-full h-40 rounded-2xl" />
          <Skeleton className="w-full h-40 rounded-2xl" />
          <Skeleton className="w-full h-40 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="flex flex-col w-full gap-8">
      {/* Section header */}
      <h3
        style={{ color: currentTheme?.primary }}
        className="!mb-6 text-lg font-bold epilogue-font"
      >
        {lang.get('msg.screeningQuotaInsights')}
      </h3>

      {/* Quota progress */}
      {data.quota.max > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium" style={{ color: currentTheme?.['primary-text'] }}>
            {data.quota.current} / {data.quota.max} {lang.get('msg.respondents')} ({Math.round(quotaPercentage)}%)
          </span>
          <div className="relative h-3 w-full overflow-hidden rounded-full bg-neutral-100">
            <div
              className={`h-full transition-all ${progressBarColor}`}
              style={{ width: `${Math.min(quotaPercentage, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Screening summary cards */}
      <div className="grid grid-cols-5 gap-4 max1100:grid-cols-3 md800:grid-cols-2 min500:grid-cols-1">
        <StatisticCard
          isLoading={false}
          bgColor={currentTheme?.primary}
          title={lang.get('msg.screeningTotalScreened')}
          tooltip={lang.get('msg.screeningTotalScreenedTooltip')}
        >
          <CountUp end={data.screening.total_screened} />
        </StatisticCard>

        <StatisticCard
          isLoading={false}
          bgColor="#059669"
          title={lang.get('msg.screeningPassed')}
          tooltip={lang.get('msg.screeningPassedTooltip')}
        >
          <CountUp end={data.screening.passed} />
        </StatisticCard>

        <StatisticCard
          isLoading={false}
          bgColor="#dc2626"
          title={lang.get('msg.screeningFailed')}
          tooltip={lang.get('msg.screeningFailedTooltip')}
        >
          <CountUp end={data.screening.failed} />
        </StatisticCard>

        <StatisticCard
          isLoading={false}
          bgColor="#f59e0b"
          title={lang.get('msg.screeningAbandoned')}
          tooltip={lang.get('msg.screeningAbandonedTooltip')}
        >
          <CountUp end={data.screening.abandoned} />
        </StatisticCard>

        <StatisticCard
          isLoading={false}
          bgColor={currentTheme?.primary}
          title={lang.get('msg.screeningPassRate')}
          tooltip={lang.get('msg.screeningPassRateTooltip')}
        >
          <CountUp end={data.screening.pass_rate} decimals={1} />%
        </StatisticCard>
      </div>

    </div>
  );
}

export default ScreeningInsights;
